/*
  SE2 · Kit quiz behaviour (2026-09-29, spec .design/specs/quiz-kit-2026-09-29.md C1, contracts D.1–D.4).

  sections/se2-kit-quiz.liquid renders the <dialog>, its JSON island and an inline boot that already opened the
  dialog when this is a first visit, and that unlocks the page and records a dismissal on close. This file adds
  everything else:

    · step 1 (space) → step 2 (fragrance, Scent | Emotion toggle) with the inert NEXT, the nudge, Enter on a radio,
      the browser Back returning to step 1, and focus on each step's <h2>;
    · the matching moment: the chosen photos meet over the feeling's halo, the answers go to storage and to the URL,
      se2:quiz:complete hands them to the kit, the kit's se2:kit:applied names the match, the result stays at least
      1800ms (or until "See your kit"), then the dialog closes with reason "complete";
    · reopening from anything with [data-se2-quiz-open] or an se2:quiz:open event, at step 1 with the saved answers;
    · the theme editor: select opens it non-modally, deselect closes it, load/unload rebind.

  Events (CustomEvent on document, bubbling): se2:quiz:open {source}, se2:quiz:close {reason} (after close() has
  returned and html[data-se2-quiz-open] is gone), se2:quiz:complete {v, space, spaceLabel, diffuser, candidates,
  feel, url, source}. Analytics through Shopify.analytics.publish when present: se2_quiz_open, se2_quiz_answer,
  se2_quiz_complete, se2_quiz_skip. Storage key se2-quiz:v1 (session and local, 30 days); every access is guarded.
*/
(() => {
  if (window.SE2Quiz) return;

  const KEY = 'se2-quiz:v1';
  const TTL = 30 * 24 * 60 * 60 * 1000;
  const EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
  const html = document.documentElement;

  const reduced = () => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  };
  const publish = (name, data) => {
    try {
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') a.publish(name, data || {});
    } catch (e) {
      /* analytics never breaks the quiz */
    }
  };
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const fill = (template, map) =>
    Object.keys(map).reduce((out, token) => out.split(token).join(map[token] == null ? '' : String(map[token])), template || '');

  const store = {
    read() {
      let v = null;
      try {
        v = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      } catch (e) {
        v = null;
      }
      if (v && v.status) return v;
      try {
        v = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (v && v.status) {
          if (v.exp > Date.now()) return v;
          localStorage.removeItem(KEY);
        }
      } catch (e) {
        /* storage blocked */
      }
      return null;
    },
    write(rec) {
      const s = JSON.stringify(rec);
      try {
        sessionStorage.setItem(KEY, s);
      } catch (e) {
        /* storage blocked */
      }
      try {
        localStorage.setItem(KEY, s);
      } catch (e) {
        /* storage blocked */
      }
    },
  };

  /* The feeling's halo token (snippets/theme-styles-variables.liquid). Moods without a token of their own fall back
     to the store's older mood names: comfort → cozy, energy → energize. Woody's family key is "wood". */
  const haloFor = (group, key) => {
    if (group === 'emotion') {
      const alias = { comfort: 'cozy', energy: 'energize' }[key] || key;
      return `var(--se2-halo-mood-${key}, var(--se2-halo-mood-${alias}, none))`;
    }
    return `var(--se2-halo-${key === 'wood' ? 'woody' : key}, none)`;
  };

  const make = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  };

  class Quiz {
    constructor(dialog) {
      this.d = dialog;
      this.id = dialog.dataset.sectionId || '';
      this.design = dialog.hasAttribute('data-se2-quiz-design') || Boolean(window.Shopify && window.Shopify.designMode);
      this.island = {};
      try {
        this.island = JSON.parse(dialog.parentNode.querySelector('[data-se2-quiz-data]').textContent) || {};
      } catch (e) {
        this.island = {};
      }
      this.L = this.island.labels || {};
      this.stepSpace = dialog.querySelector('[data-se2-quiz-step="space"]');
      this.stepFeel = dialog.querySelector('[data-se2-quiz-step="fragrance"]');
      this.matchEl = dialog.querySelector('[data-se2-quiz-step="match"]');
      this.body = dialog.querySelector('[data-se2-quiz-body]');
      this.next = dialog.querySelector('[data-se2-quiz-next]');
      this.see = dialog.querySelector('[data-se2-quiz-see]');
      this.back = dialog.querySelector('[data-se2-quiz-back]');
      this.skip = dialog.querySelector('[data-testid="se2-quiz-skip"]');
      this.barForm = dialog.querySelector('[data-se2-quiz-barform]');
      this.say = dialog.querySelector('[data-se2-quiz-say]');
      this.result = dialog.querySelector('[data-se2-quiz-result]');
      this.group = dialog.dataset.group === 'scent' ? 'scent' : 'emotion';
      this.state = dialog.open ? 'space' : 'closed';
      this.run = 0;
      this.busy = false;
      this.closing = false;
      this.pendingPop = false;
      this.popResolve = null;
      this.seeResolve = null;
      this.warmed = false;
      this.ready = { space: false, fragrance: false };
      this.swapToken = 0;
      this.setAnim = null;
      this.ac = new AbortController();
      if (!this.stepSpace || !this.stepFeel || !this.matchEl || !this.next) return;
      this.bind();
      dialog._se2Quiz = this;

      if (dialog.open) {
        // The inline boot opened it before first paint.
        this.state = 'space';
        this.update();
        const source = dialog.getAttribute('data-se2-quiz-source') || 'auto';
        publish('se2_quiz_open', { source });
        queueMicrotask(() => emit('se2:quiz:open', { source }));
      }
    }

    bind() {
      const o = { signal: this.ac.signal };
      const d = this.d;
      if (this.barForm) this.barForm.addEventListener('submit', (e) => this.onBarSubmit(e), o);
      this.stepSpace.addEventListener('submit', (e) => this.onSpaceSubmit(e), o);
      this.stepFeel.addEventListener('submit', (e) => this.onFeelSubmit(e), o);
      d.addEventListener('change', (e) => this.onChange(e), o);
      d.addEventListener('keydown', (e) => this.onKey(e), o);
      d.addEventListener(
        'click',
        (e) => {
          const seg = e.target instanceof Element ? e.target.closest('[data-se2-quiz-group]') : null;
          if (seg) this.setGroup(seg.dataset.se2QuizGroup, true);
        },
        o
      );
      if (this.back) this.back.addEventListener('click', () => this.toSpace(false), o);
      if (this.see) {
        this.see.addEventListener(
          'click',
          () => {
            if (this.seeResolve) this.seeResolve();
          },
          o
        );
      }
      d.addEventListener('close', () => this.onClose(), o);
      window.addEventListener('popstate', () => this.onPop(), o);
    }

    destroy() {
      this.ac.abort();
      this.run += 1;
      delete this.d._se2Quiz;
    }

    /* ---------- open / reset ---------- */

    open(source) {
      const d = this.d;
      if (d.open || !(this.island.spaces || []).length) return false;
      this.run += 1;
      this.reset();
      d.returnValue = '';
      if (this.design) {
        d.show();
      } else {
        html.setAttribute('data-se2-quiz-open', '');
        try {
          d.showModal();
        } catch (e) {
          d.setAttribute('open', '');
        }
      }
      // The dialog takes the first focus (tabindex -1), so no control wears a ring before anyone presses a key;
      // Tab then starts at Skip.
      d.focus({ preventScroll: true });
      this.state = 'space';
      d.setAttribute('data-se2-quiz-state', 'space');
      if (!reduced() && d.animate) d.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: EASE });
      if (!this.design) publish('se2_quiz_open', { source });
      return true;
    }

    /* A (re)open starts at step 1 with the saved answers preselected. */
    reset() {
      this.busy = false;
      this.closing = false;
      this.swapToken += 1;
      [this.stepSpace, this.stepFeel, this.matchEl].forEach((el) => {
        if (typeof el.getAnimations === 'function') el.getAnimations().forEach((a) => a.cancel());
        el.classList.remove('is-leaving');
      });
      this.stepSpace.hidden = false;
      this.stepFeel.hidden = true;
      this.matchEl.hidden = true;
      this.matchEl.classList.remove('is-in');
      if (this.result) this.result.textContent = '';
      this.next.hidden = false;
      if (this.see) {
        this.see.hidden = true;
        this.see.removeAttribute('aria-describedby');
      }
      if (this.back) this.back.hidden = true;
      if (this.skip) this.skip.hidden = false;
      this.next.setAttribute('form', this.stepSpace.id);
      this.next.textContent = this.L.next || 'Next';

      const rec = store.read();
      if (rec && rec.space) this.check('se2-quiz-space', rec.space);
      if (rec && rec.feel && (rec.feel.group === 'scent' || rec.feel.group === 'emotion')) {
        this.setGroup(rec.feel.group, false);
        this.check(`se2-quiz-${rec.feel.group}`, rec.feel.key);
      }
      this.state = 'space';
      this.d.setAttribute('aria-labelledby', 'se2-quiz-h-space');
      if (this.body) this.body.scrollTop = 0;
      this.update();
    }

    check(name, value) {
      if (this.d.querySelector(`input[name="${name}"]:checked`)) return;
      const input = [...this.d.querySelectorAll(`input[name="${name}"]`)].find((i) => i.value === String(value));
      if (input) input.checked = true;
    }

    /* ---------- state helpers ---------- */

    visibleSet() {
      return this.stepFeel.querySelector(`[data-se2-quiz-set="${this.group}"]`);
    }

    checkedIn(step) {
      if (step === 'space') return this.stepSpace.querySelector('input[name="se2-quiz-space"]:checked');
      const set = this.visibleSet();
      return set ? set.querySelector('input[type="radio"]:checked') : null;
    }

    update() {
      const step = this.state === 'fragrance' ? 'fragrance' : 'space';
      const ok = Boolean(this.checkedIn(step));
      const wasOk = this.next.getAttribute('aria-disabled') === 'false';
      this.next.setAttribute('aria-disabled', ok ? 'false' : 'true');
      if (ok && !wasOk && !this.ready[step] && !reduced()) {
        this.ready[step] = true;
        this.next.classList.remove('is-ready');
        void this.next.offsetWidth;
        this.next.classList.add('is-ready');
        setTimeout(() => this.next.classList.remove('is-ready'), 320);
      }
    }

    focusHead() {
      const id = this.state === 'fragrance' ? `se2-quiz-h-${this.group}` : 'se2-quiz-h-space';
      const h = this.d.querySelector(`#${id}`);
      if (h) h.focus({ preventScroll: true });
    }

    warm(set) {
      if (!set) return;
      set.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        if (typeof img.decode === 'function') img.decode().catch(() => {});
      });
    }

    swap(from, to, dir) {
      if (this.body) this.body.scrollTop = 0;
      to.hidden = false;
      const token = ++this.swapToken;
      to.classList.remove('is-leaving');
      if (reduced() || typeof from.animate !== 'function' || from.hidden) {
        if (from !== to) from.hidden = true;
        return;
      }
      from.classList.add('is-leaving');
      const out = from.animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: `translateX(${-24 * dir}px)` },
        ],
        { duration: 240, easing: EASE, fill: 'forwards' }
      );
      to.animate(
        [
          { opacity: 0, transform: `translateX(${24 * dir}px)` },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 240, easing: EASE, fill: 'backwards' }
      );
      out.finished
        .then(() => {
          if (token === this.swapToken) from.hidden = true;
          from.classList.remove('is-leaving');
          out.cancel();
        })
        .catch(() => {});
    }

    nudge(step) {
      const msg = step === 'space' ? this.L.needSpace : this.L.needFeel;
      if (this.say) {
        this.say.textContent = '';
        requestAnimationFrame(() => {
          this.say.textContent = msg || '';
        });
      }
      if (reduced()) return;
      const scope = step === 'space' ? this.stepSpace : this.visibleSet();
      const grid = scope && scope.querySelector('.se2-quiz__grid');
      if (!grid) return;
      grid.classList.remove('is-nudge');
      void grid.offsetWidth;
      grid.classList.add('is-nudge');
      setTimeout(() => grid.classList.remove('is-nudge'), 260);
    }

    armPopSafety() {
      clearTimeout(this.popTimer);
      this.popTimer = setTimeout(() => {
        this.pendingPop = false;
      }, 1500);
    }

    /* ---------- handlers ---------- */

    onChange(e) {
      const t = e.target;
      if (!t || t.type !== 'radio') return;
      if (t.name === 'se2-quiz-space' && !this.warmed) {
        this.warmed = true;
        this.warm(this.visibleSet());
      }
      this.update();
    }

    onKey(e) {
      if (e.key !== 'Enter' || e.isComposing) return;
      const t = e.target;
      if (!t || t.type !== 'radio' || !t.form) return;
      e.preventDefault();
      if (typeof t.form.requestSubmit === 'function') t.form.requestSubmit();
      else t.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }

    onBarSubmit(e) {
      e.preventDefault();
      const v = e.submitter && e.submitter.value === 'skip' ? 'skip' : 'close';
      this.closeWith(v, 200);
    }

    onSpaceSubmit(e) {
      e.preventDefault();
      if (this.busy || this.state !== 'space') return;
      const r = this.checkedIn('space');
      if (!r) {
        this.nudge('space');
        return;
      }
      publish('se2_quiz_answer', { step: 'space', value: r.value });
      this.toFragrance();
    }

    onFeelSubmit(e) {
      e.preventDefault();
      if (this.busy || this.state !== 'fragrance') return;
      const r = this.checkedIn('fragrance');
      if (!r) {
        this.nudge('fragrance');
        return;
      }
      this.complete(r);
    }

    onPop() {
      if (this.pendingPop) {
        this.pendingPop = false;
        const resolve = this.popResolve;
        this.popResolve = null;
        if (resolve) resolve();
        return;
      }
      if (this.d.open && this.state === 'fragrance') this.toSpace(true);
    }

    onClose() {
      const rv = this.d.returnValue;
      const reason = rv === 'complete' ? 'complete' : rv === 'skip' ? 'skip' : rv === 'close' ? 'close' : 'escape';
      const from = this.state;
      this.run += 1;
      this.busy = false;
      this.closing = false;
      this.state = 'closed';
      html.removeAttribute('data-se2-quiz-open');
      this.d.setAttribute('data-se2-quiz-state', 'closed');
      if (history.state && history.state.se2Quiz === 'fragrance') {
        this.pendingPop = true;
        this.armPopSafety();
        try {
          history.back();
        } catch (e) {
          this.pendingPop = false;
        }
      }
      if (reason !== 'complete' && !this.design) publish('se2_quiz_skip', { reason, step: from });
      emit('se2:quiz:close', { reason });
      if (reason !== 'complete' && !this.design) {
        const opener =
          document.querySelector('[data-se2-kit] [data-se2-quiz-open]') || document.querySelector('body [data-se2-quiz-open]');
        if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      }
    }

    /* ---------- steps ---------- */

    toFragrance() {
      try {
        history.pushState({ se2Quiz: 'fragrance' }, '');
      } catch (e) {
        /* history unavailable: the in-dialog Back still works */
      }
      this.swap(this.stepSpace, this.stepFeel, 1);
      this.state = 'fragrance';
      this.d.setAttribute('data-se2-quiz-state', 'fragrance');
      if (this.back) this.back.hidden = false;
      if (this.skip) this.skip.hidden = true;
      this.next.setAttribute('form', this.stepFeel.id);
      this.next.textContent = this.L.finish || 'Build your kit';
      this.d.setAttribute('aria-labelledby', `se2-quiz-h-${this.group}`);
      this.update();
      this.focusHead();
      this.warm(this.visibleSet());
      const other = this.stepFeel.querySelector(`[data-se2-quiz-set]:not([data-se2-quiz-set="${this.group}"])`);
      setTimeout(() => this.warm(other), 600);
    }

    toSpace(fromPop) {
      if (this.state !== 'fragrance' || this.busy) return;
      if (!fromPop && history.state && history.state.se2Quiz === 'fragrance') {
        this.pendingPop = true;
        this.armPopSafety();
        try {
          history.back();
        } catch (e) {
          this.pendingPop = false;
        }
      }
      this.swap(this.stepFeel, this.stepSpace, -1);
      this.state = 'space';
      this.d.setAttribute('data-se2-quiz-state', 'space');
      if (this.back) this.back.hidden = true;
      if (this.skip) this.skip.hidden = false;
      this.next.setAttribute('form', this.stepSpace.id);
      this.next.textContent = this.L.next || 'Next';
      this.d.setAttribute('aria-labelledby', 'se2-quiz-h-space');
      this.update();
      this.focusHead();
    }

    setGroup(group, animate) {
      if (group !== 'scent' && group !== 'emotion') return;
      this.group = group;
      this.d.dataset.group = group;
      this.d.querySelectorAll('[data-se2-quiz-group]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.se2QuizGroup === group));
      });
      this.d.querySelectorAll('[data-se2-quiz-head]').forEach((h) => {
        h.classList.toggle('is-current', h.dataset.se2QuizHead === group);
      });
      const sets = [...this.stepFeel.querySelectorAll('[data-se2-quiz-set]')];
      const incoming = sets.find((s) => s.dataset.se2QuizSet === group);
      const leaving = sets.filter((s) => s !== incoming && !s.hidden);
      if (this.setAnim) {
        this.setAnim.cancel();
        this.setAnim = null;
      }
      const finish = () => sets.forEach((s) => (s.hidden = s !== incoming));
      if (animate && leaving.length && !reduced() && !this.stepFeel.hidden && typeof leaving[0].animate === 'function') {
        const a = leaving[0].animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out', fill: 'forwards' });
        this.setAnim = a;
        a.finished
          .then(() => {
            if (this.setAnim !== a) return;
            this.setAnim = null;
            finish();
            a.cancel();
          })
          .catch(() => {});
      } else {
        finish();
      }
      if (this.state === 'fragrance') this.d.setAttribute('aria-labelledby', `se2-quiz-h-${group}`);
      this.warm(incoming);
      this.update();
    }

    /* ---------- the matching moment and the handoff (C1.6) ---------- */

    showMatch(spaceInput, feelInput, feel) {
      const pic = (input) => {
        const img = input.closest('label') && input.closest('label').querySelector('.se2-quiz__media img');
        return img ? img.currentSrc || img.src : '';
      };
      const place = (slot, src) => {
        const img = this.matchEl.querySelector(`[data-se2-quiz-pic="${slot}"]`);
        if (!img) return;
        const tile = img.parentElement;
        if (src) {
          img.src = src;
          tile.classList.remove('is-wash');
        } else {
          img.removeAttribute('src');
          tile.classList.add('is-wash');
        }
      };
      place('space', pic(spaceInput));
      place('feel', pic(feelInput));
      const halo = this.matchEl.querySelector('[data-se2-quiz-halo]');
      if (halo) halo.style.setProperty('--se2-quiz-halo', haloFor(feel.group, feel.key));

      this.result.textContent = '';
      this.matchEl.classList.remove('is-in');
      this.state = 'match';
      this.d.setAttribute('data-se2-quiz-state', 'match');
      this.next.hidden = true;
      if (this.back) this.back.hidden = true;
      if (this.skip) this.skip.hidden = true;
      // Unhide the live region first and write into it on the next frame: a region that appears already holding
      // its text is usually not announced. Focus goes to the result itself (tabindex -1), not an unnamed div.
      this.swap(this.stepFeel, this.matchEl, 0);
      const run = this.run;
      requestAnimationFrame(() => {
        if (this.run !== run || this.state !== 'match' || this.result.childNodes.length) return;
        this.result.appendChild(make('p', 'se2-quiz__matching', this.L.matching || 'Matching your space and feeling.'));
        this.result.focus({ preventScroll: true });
      });
      requestAnimationFrame(() => requestAnimationFrame(() => this.matchEl.classList.add('is-in')));
    }

    /** The match line, with each name kept on one line ("Crystal Rouge" never splits under text-wrap: balance). */
    matchLine(template, map) {
      const p = make('p', 'se2-quiz__r-line');
      String(template || '')
        .split(/(\[\w+\])/)
        .forEach((part) => {
          if (!part) return;
          if (Object.prototype.hasOwnProperty.call(map, part)) {
            if (map[part]) p.appendChild(make('span', 'se2-quiz__nb', map[part]));
          } else {
            p.appendChild(document.createTextNode(part));
          }
        });
      return p;
    }

    showResult(applied, space) {
      const L = this.L;
      const title = (applied && applied.diffuser && applied.diffuser.title) || space.diffuserTitle || '';
      const fr = applied && applied.fragrance;
      const line =
        fr && fr.title
          ? this.matchLine(L.matchLine || '[diffuser]. [fragrance]. Your atmosphere.', { '[diffuser]': title, '[fragrance]': fr.title })
          : this.matchLine(L.matchLineNoFragrance || '[diffuser]. Your atmosphere.', { '[diffuser]': title });
      const gift =
        fr && fr.free === true
          ? fill(L.matchGift || '', { '[size]': fr.size, '[plan_price]': fr.planPrice, '[every]': fr.planEvery })
          : '';
      this.result.textContent = '';
      this.result.appendChild(make('p', 'se2-quiz__r-eyebrow', L.matchEyebrow || 'Your match'));
      this.result.appendChild(line);
      if (gift) this.result.appendChild(make('p', 'se2-quiz__r-gift', gift.replace(/Subscribe & save/g, 'Subscribe\u00a0&\u00a0save')));
      if (this.see) {
        this.see.hidden = false;
        if (!reduced() && this.see.animate) this.see.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: EASE });
        // Focusing the button flushes speech on many screen readers: the button itself carries the match.
        if (this.result.id) this.see.setAttribute('aria-describedby', this.result.id);
        this.see.focus({ preventScroll: true });
      }
    }

    waitFor(name, ms, check, filter) {
      return new Promise((resolve) => {
        if (check && check()) {
          resolve(true);
          return;
        }
        let timer = 0;
        const done = (value) => {
          clearTimeout(timer);
          document.removeEventListener(name, on);
          resolve(value);
        };
        const on = (e) => {
          if (filter && !filter(e)) return;
          done(true);
        };
        document.addEventListener(name, on);
        timer = setTimeout(() => done(Boolean(check && check())), ms);
      });
    }

    popFragrance() {
      if (!(history.state && history.state.se2Quiz === 'fragrance')) return Promise.resolve();
      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          this.popResolve = null;
          resolve();
        };
        this.popResolve = finish;
        this.pendingPop = true;
        this.armPopSafety();
        try {
          history.back();
        } catch (e) {
          this.pendingPop = false;
          finish();
          return;
        }
        setTimeout(finish, 300);
      });
    }

    async complete(feelInput) {
      const run = this.run;
      const alive = () => this.run === run && this.d.open;
      this.busy = true;
      const t0 = performance.now();
      const spaceInput = this.checkedIn('space');
      if (!spaceInput) {
        this.busy = false;
        this.toSpace(false);
        return;
      }
      const space = (this.island.spaces || []).find((s) => s.key === spaceInput.value) || {
        key: spaceInput.value,
        label: spaceInput.dataset.label || '',
        diffuser: '',
        diffuserTitle: '',
        candidates: [],
      };
      const feel = { group: this.group, key: feelInput.value, label: feelInput.dataset.label || '' };
      publish('se2_quiz_answer', { step: 'fragrance', value: feel.key, group: feel.group });

      // 1. the match state
      this.showMatch(spaceInput, feelInput, feel);

      // 2. the answer, for this session and 30 days
      const now = Date.now();
      store.write({
        v: 1,
        status: 'done',
        space: space.key,
        diffuser: space.diffuser,
        feel: { group: feel.group, key: feel.key },
        at: now,
        exp: now + TTL,
      });

      // 3. drop the step-2 history entry, so the next Back leaves the page
      await this.popFragrance();
      if (!alive()) return;

      // 4. the answers in the URL, other parameters kept
      const url = new URL(location.href);
      if (space.diffuser) url.searchParams.set('diffuser', space.diffuser);
      url.searchParams.delete(feel.group === 'emotion' ? 'family' : 'mood');
      url.searchParams.set(feel.group === 'emotion' ? 'mood' : 'family', feel.key);
      url.searchParams.set('space', space.key);
      url.searchParams.set('tab', 'fragrances');
      url.searchParams.set('src', 'quiz');

      if (!document.querySelector('[data-se2-kit]')) {
        const dest = new URL(this.island.kitUrl || location.href, location.origin);
        url.searchParams.forEach((value, key) => dest.searchParams.set(key, value));
        location.assign(dest.href);
        return;
      }
      try {
        history.replaceState(history.state, '', url.href);
      } catch (e) {
        /* the event below still carries the answers */
      }

      // 5. hand the answers to the kit (listen for its answer first: it may reply synchronously)
      let applied = null;
      const onApplied = (e) => {
        if (e.detail && e.detail.source === 'quiz') applied = e.detail;
      };
      document.addEventListener('se2:kit:applied', onApplied);
      const kitOn = () => Boolean(document.querySelector('[data-se2-kit][data-js="on"]'));
      if (!kitOn()) {
        const ok = await this.waitFor('se2:kit:ready', 1500, kitOn);
        if (!ok) {
          document.removeEventListener('se2:kit:applied', onApplied);
          if (alive()) location.reload();
          return;
        }
      }
      if (!alive()) {
        document.removeEventListener('se2:kit:applied', onApplied);
        return;
      }
      const detail = {
        v: 1,
        space: space.key,
        spaceLabel: space.label,
        diffuser: space.diffuser,
        candidates: (space.candidates || []).slice(),
        feel,
        url: url.href,
        source: 'quiz',
      };
      emit('se2:quiz:complete', detail);
      publish('se2_quiz_complete', { space: space.key, diffuser: space.diffuser, group: feel.group, feel: feel.key });

      // 6. the kit names the match (at most 1200ms)
      if (!applied) {
        await this.waitFor('se2:kit:applied', 1200, () => Boolean(applied), (e) => Boolean(e.detail && e.detail.source === 'quiz'));
      }
      document.removeEventListener('se2:kit:applied', onApplied);
      const hold = 700 - (performance.now() - t0);
      if (hold > 0) await sleep(hold);
      if (!alive()) return;
      this.showResult(applied, space);

      // 7. the result stays at least 1800ms, in both motion modes, unless "See your kit" is pressed
      const pressed = new Promise((resolve) => {
        this.seeResolve = resolve;
      });
      await Promise.race([sleep(1800), pressed]);
      this.seeResolve = null;
      if (!alive()) return;
      await this.closeWith('complete', 240);
    }

    async closeWith(value, ms) {
      const d = this.d;
      if (!d.open || this.closing) return;
      this.closing = true;
      let a = null;
      if (!reduced() && typeof d.animate === 'function') {
        a = d.animate(
          [
            { opacity: 1, transform: 'none' },
            { opacity: 0, transform: value === 'complete' ? 'scale(0.985)' : 'none' },
          ],
          { duration: ms, easing: EASE, fill: 'forwards' }
        );
        try {
          await a.finished;
        } catch (e) {
          /* cancelled */
        }
      }
      if (d.open) d.close(value);
      if (a) a.cancel();
    }
  }

  /* ---------- page wiring ---------- */

  const quizzes = new Set();
  const current = () => {
    for (const q of quizzes) if (q.d.isConnected) return q;
    return null;
  };
  const byId = (id) => {
    for (const q of quizzes) if (q.id === id) return q;
    return null;
  };
  const init = (dialog) => {
    if (dialog._se2Quiz) return dialog._se2Quiz;
    const q = new Quiz(dialog);
    if (!dialog._se2Quiz) return null;
    quizzes.add(q);
    return q;
  };

  document.addEventListener('click', (e) => {
    // html[data-se2-quiz-open] is the scroll-lock flag, not an opener, so it is never matched here.
    const t = e.target instanceof Element ? e.target.closest('[data-se2-quiz-open]:not(html)') : null;
    if (!t || t.closest('dialog[data-se2-quiz]') || !current()) return;
    e.preventDefault();
    emit('se2:quiz:open', { source: 'button' });
  });
  document.addEventListener('se2:quiz:open', (e) => {
    const q = current();
    if (!q || q.d.open) return;
    q.open((e.detail && e.detail.source) || 'button');
  });

  let selectedId = null;
  document.addEventListener('shopify:section:select', (e) => {
    const q = byId(e.detail && e.detail.sectionId);
    if (!q) return;
    selectedId = q.id;
    q.open('editor');
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const q = byId(e.detail && e.detail.sectionId);
    if (!q) return;
    if (selectedId === q.id) selectedId = null;
    if (q.d.open) q.d.close('close');
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const q = byId(e.detail && e.detail.sectionId);
    if (!q) return;
    html.removeAttribute('data-se2-quiz-open');
    q.destroy();
    quizzes.delete(q);
  });
  document.addEventListener('shopify:section:load', (e) => {
    const dialog = e.target && e.target.querySelector ? e.target.querySelector('dialog[data-se2-quiz]') : null;
    if (!dialog) return;
    const q = init(dialog);
    if (q && selectedId === q.id) q.open('editor');
  });

  document.querySelectorAll('dialog[data-se2-quiz]').forEach(init);
  window.SE2Quiz = { open: (source) => emit('se2:quiz:open', { source: source || 'button' }) };
})();
